import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Badge,
  ListGroup,
  Spinner,
} from "react-bootstrap";
import WhatsAppAPI from "../../api/WhatsAppAPI";

import { toast } from "react-toastify";

const TagAssignmentModal = ({
  show,
  onHide,
  lead,
  onTagUpdate,
  refreshLeadData,
}) => {
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [creatingTag, setCreatingTag] = useState(false);

  // Fetch all available tags
  useEffect(() => {
    if (show && lead) {
      fetchTags();
      // Initialize selected tags from lead data
      if (lead.tag_names && Array.isArray(lead.tag_names)) {
        setSelectedTags(lead.tag_names.map((tag) => tag.id));
      }
    }
  }, [show, lead]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const result = await WhatsAppAPI.getTags();
      if (result.success) {
        setTags(result.records);
      } else {
        setTags([]);
        // toast.error("Failed to fetch tags");
      }
    } catch (error) {
      console.error("Error fetching tags:", error);
      toast.error("Error loading tags");
    } finally {
      setLoading(false);
    }
  };

  // Filter tags based on search
  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Handle tag selection
  const handleTagSelect = (tagId) => {
    setSelectedTags((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
  };

  // Create new tag
  const handleCreateTag = async () => {
    if (!newTag.trim()) {
      toast.error("Please enter a tag name");
      return;
    }

    try {
      setCreatingTag(true);
      const result = await WhatsAppAPI.createTag({
        name: newTag.trim(),
        status: true,
        first_message: "No",
      });

      if (result.success) {
        // Add new tag to list
        const newTagObj = { id: result.record.id, name: newTag.trim() };
        setTags((prev) => [...prev, newTagObj]);
        // Select the new tag
        setSelectedTags((prev) => [...prev, newTagObj.id]);
        setNewTag("");
        toast.success("Tag created successfully");
      } else {
        toast.error(result.message || "Failed to create tag");
      }
    } catch (error) {
      console.error("Error creating tag:", error);
      toast.error("Error creating tag");
    } finally {
      setCreatingTag(false);
    }
  };

  // Save tag assignments
  // Save tag assignments
  const handleSave = async () => {
    if (!lead) return;

    try {
      setLoading(true);

      // Get selected tag objects
      const selectedTagObjects = tags
        .filter((tag) => selectedTags.includes(tag.id))
        .map((tag) => ({ id: tag.id, name: tag.name }));

      // Update lead with new tags - Use updateLead API instead of updateLeadTags
      const result = await WhatsAppAPI.updateLead({
        ...lead, // Send the complete lead object
        tag_names: selectedTagObjects,
      });

      if (result.success) {
        toast.success("Tags updated successfully");
        if (onTagUpdate) onTagUpdate(selectedTagObjects);
        // if (refreshLeadData) refreshLeadData();
        onHide();
      } else {
        toast.error(result.message || "Failed to update tags");
      }
    } catch (error) {
      console.error("Error updating tags:", error);
      toast.error("Error updating tags");
    } finally {
      setLoading(false);
    }
  }; // Get selected tag names for display
  const getSelectedTagNames = () => {
    return tags
      .filter((tag) => selectedTags.includes(tag.id))
      .map((tag) => tag.name);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-tags me-2"></i>
          Manage Tags for {lead?.firstname} {lead?.lastname}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Selected Tags Preview */}
        <div className="mb-4">
          <h6>Selected Tags:</h6>
          <div className="d-flex flex-wrap gap-2">
            {getSelectedTagNames().length > 0 ? (
              getSelectedTagNames().map((tagName, index) => (
                <Badge key={index} bg="primary" pill className="px-3 py-2">
                  <i className="fas fa-tag me-1"></i>
                  {tagName}
                </Badge>
              ))
            ) : (
              <span className="text-muted">No tags selected</span>
            )}
          </div>
        </div>

        {/* Create New Tag Section */}
        <div className="mb-4">
          <h6>Create New Tag:</h6>
          <div className="d-flex gap-2">
            <Form.Control
              type="text"
              placeholder="Enter new tag name"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") handleCreateTag();
              }}
            />
            <Button
              variant="outline-primary"
              onClick={handleCreateTag}
              disabled={creatingTag || !newTag.trim()}
            >
              {creatingTag ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <i className="fas fa-plus me-1"></i>
                  Create
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Search Tags */}
        <div className="mb-3">
          <Form.Control
            type="text"
            placeholder="Search tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Tags List */}
        <div
          className="border rounded"
          style={{ maxHeight: "300px", overflowY: "auto" }}
        >
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading tags...</p>
            </div>
          ) : filteredTags.length > 0 ? (
            <ListGroup variant="flush">
              {filteredTags.map((tag) => (
                <ListGroup.Item
                  key={tag.id}
                  className="d-flex justify-content-between align-items-center cursor-pointer"
                  onClick={() => handleTagSelect(tag.id)}
                  style={{ cursor: "pointer" }}
                >
                  <div>
                    <i className="fas fa-tag me-2 text-secondary"></i>
                    {tag.name}
                  </div>
                  <Form.Check
                    type="checkbox"
                    checked={selectedTags.includes(tag.id)}
                    onChange={() => handleTagSelect(tag.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-center p-4 text-muted">
              {searchTerm
                ? "No tags found matching your search"
                : "No tags available"}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TagAssignmentModal;
